import {
  Button,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Typography,
  Chip,
  TextField,
  Autocomplete,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { RecordVoiceOver } from '@mui/icons-material'
import { Field, Form, Formik, FormikHelpers } from 'formik'
import { CheckboxWithLabel } from 'formik-mui'
import _ from 'lodash'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'
import { ReferralContactType } from 'src/models/enums/ReferralContactType.enum'
import { DropdownOption } from 'src/viewModels/DropdownOption'
import { ReferralContactDTO } from 'src/viewModels/ReferralContactDTO'
import * as Yup from 'yup'
import PatientActionBar from '../../common/ActionBar/PatientActionBar'
import CardWithTitle from '../../common/CardWithTitle/CardWithTitle'
import FullWidthField from '../../common/FullWidthField'
import ResponsiveAppBar from '../../common/ResponsiveAppBar'
import { useUnsavedChangesWarning } from '../../common/hooks/useUnsavedChangesWarning'
import UnsavedChangesDialog from '../../common/UnsavedChangesDialog/UnsavedChangesDialog'
import { DefaultReferral, IContact, IPatient, IReferral } from '../../Definitions'
import { IStores } from '../../Stores'
import {
  createIReferralFormValues,
  IReferralFormValues,
  toIReferral,
} from './ReferralFormValues'
import States from 'src/utils/States'

function parseDateString(_value: Date, originalValue: string) {
  return new Date(moment(originalValue, 'YYYY-MM-DD').toISOString())
}

const ReferralSchema = Yup.object().shape({
  claimNumber: Yup.string(),
  doi: Yup.date()
    .transform(parseDateString)
    .min(new Date('1-1-1900'), 'Please enter a complete date')
    .required('Date of Injury is required'),
  health: Yup.boolean(),
  initials: Yup.string(),
  occupation: Yup.string(),
  referralCaller: Yup.string(),
  referralDate: Yup.date()
    .transform(parseDateString)
    .min(new Date('1-1-1900'), 'Please enter a complete date')
    .required('Referral date is required'),
  referralIssue: Yup.boolean(),
  referralTime: Yup.string(),
})

interface IUnsavedChangesProps {
  showCancelDialog: boolean
  handleCancelClick: (hasChanges: boolean) => void
  handleCancelConfirm: (onNavigate: () => void) => void
  handleCancelDismiss: () => void
  setIsDirty: (isDirty: boolean) => void
}

interface IEditReferralProps extends IUnsavedChangesProps {
  patientId: string
  referralId: string
  employerContacts?: IContact[]
  getEmployerContacts?: (id: string, filter: string) => Promise<void>
  getPatientById?: (id: string) => Promise<void>
  getReferralById?: (id: string) => Promise<void>
  employerOptions?: DropdownOption[]
  getEmployerOptions?: (filter?: string) => Promise<void>
  isLoading?: boolean
  isModalOpen?: boolean
  openDialogWithContact?: (contact: IContact) => void
  referral?: IReferral
  referralEditFinished?: () => void
  rerouteToPath?: (path: string) => void
  saveReferral?: (referral: IReferral) => Promise<void>
  setSelectedReferral?: (referral: IReferral | undefined) => void
  insuranceCompanyOptions: DropdownOption[]
  getInsuranceCompanyOptions: () => Promise<void>
  filterInsuranceCompanyOptions: (filter: string) => void
  getNCMCompanyOptions: () => Promise<void>
  filterNCMCompanyOptions: (filter: string) => void
  ncmCompanyOptions: DropdownOption[]
  getInsuranceCompanyContactOptions: (companyId: string) => Promise<void>
  getNCMContactOptions: (companyId: string) => Promise<void>
  ncmContactOptions: DropdownOption[]
  insuranceCompanyContactOptions: DropdownOption[]
  patient: IPatient
}

class EditReferral extends React.Component<IEditReferralProps> {
  private _isMounted = false

  state = {
    isLoading: false,
    reload: false,
    disableNCM: true,
    disableInsurance: true,
    initialInsurance: undefined,
    initialNCM: undefined,
    selectedInsuranceContactId: undefined as string | undefined,
    selectedNCMContactId: undefined as string | undefined,
    selectedInsuranceContacts: new Map<string, string>(),
    selectedNCMContacts: new Map<string, string>(),
    dirtyInsurance: false,
    oldInsuranceContacts: '',
    initialInsuranceContacts: '',
    dirtyNCM: false,
    oldNCMContacts: '',
    initialNCMContacts: '',
    initialJurisdiction: '',
    selectedJurisdiction: '',
  }

  private async setup() {
    this.setState({ isLoading: true, reload: false })
    let promises = [] as Array<Promise<void>>

    promises.push(this.props.getEmployerOptions!())
    promises.push(this.props.getInsuranceCompanyOptions!())
    promises.push(this.props.getNCMCompanyOptions!())

    try {
      if (this.props.referralId) {
        promises.push(this.props.getReferralById!(this.props.referralId))
      } else {
        this.props.setSelectedReferral!(DefaultReferral(true))

        this.setState({
          selectedInsuranceContactId: undefined,
          selectedInsuranceContacts: new Map<string, string>(),
          selectedNCMContactId: undefined,
          selectedNCMContacts: new Map<string, string>(),
          initialInsurance: undefined,
          initialNCM: undefined,
          disableInsurance: true,
          disableNCM: true,
          initialJurisdiction: '',
          selectedJurisdiction: '',
          oldInsuranceContacts: '',
          initialInsuranceContacts: '',
          oldNCMContacts: '',
          initialNCMContacts: '',
        })
      }
      if (this.props.patientId) {
        promises.push(this.props.getPatientById!(this.props.patientId))
      }

      await Promise.all(promises).then(async () => {
        if (this.props.referral && !this.props.referral.isNew) {
          const jurisdiction = this.props.referral.jurisdiction || ''
          this.setState({
            selectedJurisdiction: jurisdiction,
            initialJurisdiction: jurisdiction,
          })

          const contactPromises = [] as Array<Promise<void>>

          if (this.props.referral.nurseCaseManagerId) {
            contactPromises.push(
              this.props.getNCMContactOptions(this.props.referral.nurseCaseManagerId)
            )
          }

          if (this.props.referral.insuranceCompanyId) {
            contactPromises.push(
              this.props.getInsuranceCompanyContactOptions(
                this.props.referral.insuranceCompanyId
              )
            )
          }

          await Promise.all(contactPromises).then(() => {
            if (this.props.referral?.insuranceCompanyId) {
              const insuranceContacts = new Map<string, string>()
              if (
                this.props.referral.additionalInsuranceContacts &&
                this.props.insuranceCompanyContactOptions &&
                this.props.insuranceCompanyContactOptions.length > 0
              ) {
                this.props.referral.additionalInsuranceContacts.forEach((contact) => {
                  if (
                    this.props.insuranceCompanyContactOptions?.some(
                      (opt) => opt.id === contact.contactId
                    )
                  ) {
                    insuranceContacts.set(contact.contactId, contact.contactName)
                  }
                })
              }

              this.setState({
                selectedInsuranceContactId:
                  this.props.referral?.insuranceCompanyContactId,
                selectedInsuranceContacts: insuranceContacts,
                disableInsurance: false,
                oldInsuranceContacts: JSON.stringify(
                  Array.from(new Map([...insuranceContacts].sort()))
                ),
                initialInsuranceContacts: JSON.stringify(
                  Array.from(new Map([...insuranceContacts].sort()))
                ),
              })
            }

            if (this.props.referral?.nurseCaseManagerId) {
              const ncmContacts = new Map<string, string>()
              if (
                this.props.referral.additionalNCMContacts &&
                this.props.ncmContactOptions &&
                this.props.ncmContactOptions.length > 0
              ) {
                this.props.referral.additionalNCMContacts.forEach((contact) => {
                  if (
                    this.props.ncmContactOptions?.some(
                      (opt) => opt.id === contact.contactId
                    )
                  ) {
                    ncmContacts.set(contact.contactId, contact.contactName)
                  }
                })
              }

              this.setState({
                selectedNCMContactId: this.props.referral?.nurseCaseManagerContactId,
                selectedNCMContacts: ncmContacts,
                disableNCM: false,
                oldNCMContacts: JSON.stringify(
                  Array.from(new Map([...ncmContacts].sort()))
                ),
                initialNCMContacts: JSON.stringify(
                  Array.from(new Map([...ncmContacts].sort()))
                ),
              })
            }
          })
        }

        this.setState({ isLoading: false })
      })
    } catch (error) {
      console.error('Error in setup:', error)
      if (this._isMounted) {
        this.setState({ isLoading: false })
      }
    }
  }

  public async componentDidMount() {
    this._isMounted = true
    await this.setup()
  }

  public async componentDidUpdate(prevProps: IEditReferralProps) {
    if (this.props.referralId !== prevProps.referralId) {
      await this.setup()
    }

    if (
      this.props.insuranceCompanyContactOptions !==
        prevProps.insuranceCompanyContactOptions &&
      this.props.insuranceCompanyContactOptions &&
      this.props.insuranceCompanyContactOptions.length > 0 &&
      !this.state.disableInsurance &&
      this.props.referral &&
      this.props.referral.additionalInsuranceContacts &&
      this.props.referral.additionalInsuranceContacts.length > 0
    ) {
      const contacts = new Map<string, string>()
      this.props.referral.additionalInsuranceContacts.forEach((contact) => {
        contacts.set(contact.contactId, contact.contactName)
      })

      const allExist = Array.from(contacts.keys()).every((id) =>
        this.props.insuranceCompanyContactOptions?.some((opt) => opt.id === id)
      )

      if (allExist) {
        this.setState({
          selectedInsuranceContacts: contacts,
          oldInsuranceContacts: JSON.stringify(Array.from(new Map([...contacts].sort()))),
          initialInsuranceContacts: JSON.stringify(
            Array.from(new Map([...contacts].sort()))
          ),
        })
      }
    }

    if (
      this.props.ncmContactOptions !== prevProps.ncmContactOptions &&
      this.props.ncmContactOptions &&
      this.props.ncmContactOptions.length > 0 &&
      !this.state.disableNCM &&
      this.props.referral &&
      this.props.referral.additionalNCMContacts &&
      this.props.referral.additionalNCMContacts.length > 0
    ) {
      const contacts = new Map<string, string>()
      this.props.referral.additionalNCMContacts.forEach((contact) => {
        contacts.set(contact.contactId, contact.contactName)
      })

      const allExist = Array.from(contacts.keys()).every((id) =>
        this.props.ncmContactOptions?.some((opt) => opt.id === id)
      )

      if (allExist) {
        this.setState({
          selectedNCMContacts: contacts,
          oldNCMContacts: JSON.stringify(Array.from(new Map([...contacts].sort()))),
          initialNCMContacts: JSON.stringify(Array.from(new Map([...contacts].sort()))),
        })
      }
    }
  }

  public componentWillUnmount() {
    this._isMounted = false
    this.props.setSelectedReferral!(undefined)
  }

  public handleSave = (submitForm: () => void, reload: boolean) => () => {
    this.setState({ reload }, () => {
      submitForm()
    })
  }

  private setAdditionalContacts = (type: ReferralContactType, referral: IReferral) => {
    const additionalList = [] as ReferralContactDTO[]
    if (type == ReferralContactType.Insurance) {
      this.state.selectedInsuranceContacts.forEach((contactName, contactId) => {
        if (contactName) {
          additionalList.push({
            referralId: referral.id,
            contactId: contactId,
            contactType: type,
            contactName: contactName,
          })
        }
      })
    } else {
      this.state.selectedNCMContacts.forEach((contactName, contactId) => {
        if (contactName) {
          additionalList.push({
            referralId: referral.id,
            contactId: contactId,
            contactType: type,
            contactName: contactName,
          })
        }
      })
    }
    return additionalList
  }

  public save = async (
    values: IReferralFormValues,
    formikBag: FormikHelpers<IReferralFormValues>
  ) => {
    const referral = toIReferral(values, this.props.referral)
    if (referral.insuranceCompanyId) {
      referral.insuranceCompanyContactId = this.state.selectedInsuranceContactId
    } else {
      referral.insuranceCompanyContactId = undefined
    }

    if (referral.nurseCaseManagerId) {
      referral.nurseCaseManagerContactId = this.state.selectedNCMContactId
    } else {
      referral.nurseCaseManagerContactId = undefined
    }

    referral.additionalInsuranceContacts = this.setAdditionalContacts(
      ReferralContactType.Insurance,
      referral
    )
    referral.additionalNCMContacts = this.setAdditionalContacts(
      ReferralContactType.NCM,
      referral
    )
    referral.patientId = this.props.patient.id
    referral.jurisdiction = this.state.selectedJurisdiction

    await this.props.saveReferral!(referral)
      .then(() => {
        formikBag.setSubmitting(false)
        if (this.state.reload) {
          this.props.rerouteToPath!(
            `/patients/patient/${this.props.patientId}/referral/${referral.id}`
          )
          this.setState({
            dirtyInsurance: false,
            dirtyNCM: false,
          })
          this.setup()
        } else {
          this.cancel()
        }
      })
      .catch(() => formikBag.setSubmitting(false))
  }

  private hasUnsavedChanges = (dirty: boolean) => {
    return (
      this.state.dirtyInsurance ||
      this.state.dirtyNCM ||
      this.state.selectedNCMContactId !==
        this.props.referral?.nurseCaseManagerContactId ||
      this.state.selectedInsuranceContactId !==
        this.props.referral?.insuranceCompanyContactId ||
      this.state.selectedJurisdiction !== this.state.initialJurisdiction ||
      dirty
    )
  }

  private handleCancelClick = (dirty: boolean) => {
    const hasChanges = this.hasUnsavedChanges(dirty)
    if (hasChanges) {
      this.props.handleCancelClick(hasChanges)
    } else {
      this.cancel()
    }
  }

  private cancel = () => {
    this.props.rerouteToPath!(`/patients/patient/${this.props.patientId}`)
  }

  private getSelectedInsurance(values: IReferralFormValues) {
    return _.find(
      this.props.insuranceCompanyOptions,
      (x: DropdownOption) => x.id === values.insuranceCompanyId
    )
  }

  private getSelectedNCM(values: IReferralFormValues) {
    return _.find(
      this.props.ncmCompanyOptions,
      (x: DropdownOption) => x.id === values.nurseCaseManagerId
    )
  }

  public updateNCMContacts = async (value: any) => {
    if (!value) {
      this.setState({
        disableNCM: true,
        selectedNCMContactId: undefined,
        selectedNCMContacts: new Map<string, string>(),
      })
      return
    }

    this.setState({
      disableNCM: true,
      selectedNCMContactId: undefined,
      selectedNCMContacts: new Map<string, string>(),
    })

    try {
      await this.props.getNCMContactOptions(value.id)
    } finally {
      if (this._isMounted) {
        this.setState({
          disableNCM: false,
        })
      }
    }
  }

  public updateInsuranceContacts = async (value: any) => {
    if (!value) {
      this.setState({
        disableInsurance: true,
        selectedInsuranceContactId: undefined,
        selectedInsuranceContacts: new Map<string, string>(),
      })
      return
    }

    this.setState({
      disableInsurance: true,
      selectedInsuranceContactId: undefined,
      selectedInsuranceContacts: new Map<string, string>(),
    })

    try {
      await this.props.getInsuranceCompanyContactOptions(value.id)
    } finally {
      if (this._isMounted) {
        this.setState({
          disableInsurance: false,
        })
      }
    }
  }

  public forceUpdateHandler(
    insuranceContacts?: Map<string, string>,
    ncmContacts?: Map<string, string>
  ) {
    const newInsuranceTags = JSON.stringify(
      Array.from(
        new Map([...(insuranceContacts || this.state.selectedInsuranceContacts)].sort())
      )
    )
    const newNCMTags = JSON.stringify(
      Array.from(new Map([...(ncmContacts || this.state.selectedNCMContacts)].sort()))
    )

    this.setState({
      dirtyInsurance: newInsuranceTags !== this.state.initialInsuranceContacts,
      oldInsuranceContacts: newInsuranceTags,
      dirtyNCM: newNCMTags !== this.state.initialNCMContacts,
      oldNCMContacts: newNCMTags,
    })
  }

  public render() {
    const { referral, insuranceCompanyOptions, ncmCompanyOptions } = this.props

    const title = referral && referral.isNew ? 'Add New Referral' : 'Edit Referral'
    return (
      <>
        <Helmet>
          <title>
            {title +
              ': ' +
              this.props.patient?.lastName +
              ', ' +
              this.props.patient?.firstName +
              ' (' +
              moment(this.props.patient?.dateOfBirth).format('MM/DD/YYYY') +
              ')'}
          </title>
        </Helmet>
        {referral ? (
          <Formik
            initialValues={createIReferralFormValues(referral)}
            onSubmit={this.save}
            validationSchema={ReferralSchema}
            enableReinitialize={true}
          >
            {({ errors, isValid, values, setFieldValue, submitForm, dirty }) => {
              const totalIsDirty = this.hasUnsavedChanges(dirty)

              React.useEffect(() => {
                this.props.setIsDirty(totalIsDirty)
              }, [totalIsDirty])

              return (
                <>
                  <Form>
                    {this.state.isLoading && (
                      <div
                        style={{
                          position: 'fixed',
                          display: 'flex',
                          width: '100%',
                          height: '100%',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          cursor: 'pointer',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: '1200',
                        }}
                      >
                        <CircularProgress size={100} />
                      </div>
                    )}
                    <ResponsiveAppBar title={title} pageIcon={<RecordVoiceOver />}>
                      <Grid
                        container
                        direction="row"
                        spacing={2}
                        justifyContent="flex-end"
                      >
                        <Grid item>
                          <Button
                            size="small"
                            variant="contained"
                            style={{ backgroundColor: '#e0e0e0', color: '#000' }}
                            onClick={() => this.handleCancelClick(dirty)}
                          >
                            Cancel
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            size="small"
                            color="secondary"
                            variant="contained"
                            disabled={
                              !isValid ||
                              (!this.state.dirtyInsurance &&
                                !this.state.dirtyNCM &&
                                this.state.selectedNCMContactId ===
                                  this.props.referral?.nurseCaseManagerContactId &&
                                this.state.selectedInsuranceContactId ===
                                  this.props.referral?.insuranceCompanyContactId &&
                                this.state.selectedJurisdiction ===
                                  this.state.initialJurisdiction &&
                                !dirty)
                            }
                            onClick={this.handleSave(submitForm, true)}
                          >
                            Save
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            size="small"
                            color="secondary"
                            variant="contained"
                            disabled={
                              !isValid ||
                              (!this.state.dirtyInsurance &&
                                !this.state.dirtyNCM &&
                                this.state.selectedNCMContactId ===
                                  this.props.referral?.nurseCaseManagerContactId &&
                                this.state.selectedInsuranceContactId ===
                                  this.props.referral?.insuranceCompanyContactId &&
                                this.state.selectedJurisdiction ===
                                  this.state.initialJurisdiction &&
                                !dirty)
                            }
                            onClick={this.handleSave(submitForm, false)}
                          >
                            Save and Close
                          </Button>
                        </Grid>
                      </Grid>
                    </ResponsiveAppBar>
                    <PatientActionBar />

                    {!this.state.isLoading ? (
                      <Grid
                        container
                        spacing={3}
                        direction="row"
                        justifyContent="space-between"
                        sx={{ mt: 2, pl: 2, pr: 2 }}
                      >
                        <Grid item lg={8} md={12}>
                          <CardWithTitle title="Referral" icon={RecordVoiceOver}>
                            <Grid item md={4} xs={12}>
                              <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                  label="DOI"
                                  value={values.doi ? moment(values.doi) : null}
                                  onChange={(newValue) => {
                                    setFieldValue(
                                      'doi',
                                      newValue ? newValue.toDate() : null
                                    )
                                  }}
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      variant: 'outlined',
                                      size: 'small',
                                      InputLabelProps: { shrink: true },
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item md={4} xs={12}>
                              <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                  label="Referral Date"
                                  value={
                                    values.referralDate
                                      ? moment(values.referralDate)
                                      : null
                                  }
                                  onChange={(newValue) => {
                                    setFieldValue(
                                      'referralDate',
                                      newValue ? newValue.toDate() : null
                                    )
                                  }}
                                  slotProps={{
                                    textField: {
                                      fullWidth: true,
                                      variant: 'outlined',
                                      size: 'small',
                                      required: true,
                                      InputLabelProps: { shrink: true },
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item md={4} xs={12}>
                              <FullWidthField
                                InputProps={{
                                  style: { height: 40 },
                                }}
                                variant="outlined"
                                type="outlined"
                                name="referralTime"
                                label="Referral Time"
                              />
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <Autocomplete
                                size="small"
                                id="employerId"
                                options={this.props.employerOptions || []}
                                getOptionLabel={(option: DropdownOption) =>
                                  option.description || ''
                                }
                                isOptionEqualToValue={(option, value) =>
                                  option.id === value.id
                                }
                                value={
                                  (this.props.employerOptions || []).find(
                                    (x) => x.id === values.employerId
                                  ) || null
                                }
                                onChange={(_, newValue) => {
                                  setFieldValue('employer', newValue?.description || '')
                                  setFieldValue('employerId', newValue?.id || undefined)
                                }}
                                renderOption={(props, option: DropdownOption) => (
                                  <li {...props} key={option.id}>
                                    {option.description}
                                    {!option.isEnabled && ' (Inactive)'}
                                  </li>
                                )}
                                filterOptions={(options, state) => {
                                  const inputValue = state.inputValue.toLowerCase()
                                  return options.filter((option) =>
                                    option.description.toLowerCase().includes(inputValue)
                                  )
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Employer"
                                    variant="outlined"
                                    error={!!errors.employerId}
                                    helperText={errors.employerId}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <FullWidthField
                                InputProps={{
                                  style: { height: 40 },
                                }}
                                variant="outlined"
                                type="outlined"
                                name="occupation"
                                label="Occupation"
                              />
                            </Grid>
                            <Grid
                              container
                              item
                              direction="row"
                              lg={12}
                              spacing={2}
                              justifyContent="space-between"
                              alignItems="flex-start"
                              style={{ marginBottom: '10px' }}
                            >
                              <Grid
                                container
                                item
                                lg={6}
                                spacing={2}
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <Grid item style={{ marginTop: '16px' }}>
                                  <Typography
                                    variant={'body1'}
                                    color="textSecondary"
                                    style={{
                                      height: '18px',
                                      paddingRight: '8px',
                                      marginBottom: '4px',
                                    }}
                                  >
                                    INSURANCE
                                  </Typography>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <Grid item md={12} xs={12}>
                                    <Autocomplete
                                      size="small"
                                      id="insuranceCompanyId"
                                      options={insuranceCompanyOptions || []}
                                      getOptionLabel={(option: DropdownOption) =>
                                        option.description || ''
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                      }
                                      value={this.getSelectedInsurance(values) || null}
                                      onChange={(_, newValue) => {
                                        setFieldValue(
                                          'insuranceCompany',
                                          newValue?.description || ''
                                        )
                                        setFieldValue(
                                          'insuranceCompanyId',
                                          newValue?.id || undefined
                                        )
                                        this.updateInsuranceContacts(newValue)
                                      }}
                                      renderOption={(props, option: DropdownOption) => (
                                        <li {...props} key={option.id}>
                                          {option.description}
                                          {!option.isEnabled && ' (Inactive)'}
                                        </li>
                                      )}
                                      filterOptions={(options, state) => {
                                        const inputValue = state.inputValue.toLowerCase()
                                        return options.filter((option) =>
                                          option.description
                                            .toLowerCase()
                                            .includes(inputValue)
                                        )
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Insurance Company"
                                          variant="outlined"
                                          error={!!errors.insuranceCompanyId}
                                          helperText={errors.insuranceCompanyId}
                                          InputLabelProps={{ shrink: true }}
                                        />
                                      )}
                                    />
                                  </Grid>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <FormControl fullWidth variant="outlined">
                                    <InputLabel shrink={true} variant="outlined">
                                      Primary Insurance Company Contact
                                    </InputLabel>
                                    <Autocomplete
                                      size="small"
                                      id="insuranceCompanyContactId"
                                      options={
                                        this.props.insuranceCompanyContactOptions || []
                                      }
                                      getOptionLabel={(option: DropdownOption) =>
                                        option.description || ''
                                      }
                                      getOptionDisabled={(option: DropdownOption) =>
                                        !option.isEnabled
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                      }
                                      value={
                                        (
                                          this.props.insuranceCompanyContactOptions || []
                                        ).find(
                                          (x) =>
                                            x.id === this.state.selectedInsuranceContactId
                                        ) || null
                                      }
                                      onChange={(_, newValue) => {
                                        this.setState({
                                          selectedInsuranceContactId:
                                            newValue?.id || undefined,
                                          selectedInsuranceContacts: new Map<
                                            string,
                                            string
                                          >(),
                                        })
                                      }}
                                      disabled={this.state.disableInsurance}
                                      renderOption={(props, option: DropdownOption) => (
                                        <li {...props} key={option.id}>
                                          {option.description}
                                          {!option.isEnabled && ' (Inactive)'}
                                        </li>
                                      )}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Primary Insurance Company Contact"
                                          variant="outlined"
                                          InputLabelProps={{ shrink: true }}
                                        />
                                      )}
                                    />
                                  </FormControl>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <FormControl fullWidth variant="outlined">
                                    <InputLabel shrink={true} variant="outlined">
                                      Additional Insurance Company Contacts
                                    </InputLabel>
                                    <Autocomplete
                                      size="small"
                                      multiple
                                      options={(
                                        this.props.insuranceCompanyContactOptions || []
                                      ).filter(
                                        (x) =>
                                          x.id !==
                                            this.state.selectedInsuranceContactId &&
                                          !this.state.selectedInsuranceContacts.has(x.id)
                                      )}
                                      getOptionLabel={(option) =>
                                        option.description || ''
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                      }
                                      value={(() => {
                                        if (
                                          this.state.selectedInsuranceContacts.size ===
                                            0 ||
                                          !this.props.insuranceCompanyContactOptions ||
                                          this.props.insuranceCompanyContactOptions
                                            .length === 0
                                        ) {
                                          return []
                                        }

                                        return this.props.insuranceCompanyContactOptions.filter(
                                          (option) =>
                                            this.state.selectedInsuranceContacts.has(
                                              option.id
                                            )
                                        )
                                      })()}
                                      onChange={(_, newValue) => {
                                        const newContacts = new Map<string, string>()
                                        newValue.forEach((option) => {
                                          newContacts.set(option.id, option.description)
                                        })
                                        this.setState(
                                          {
                                            selectedInsuranceContacts: newContacts,
                                          },
                                          () => {
                                            this.forceUpdateHandler(
                                              newContacts,
                                              undefined
                                            )
                                          }
                                        )
                                      }}
                                      disabled={
                                        this.state.disableInsurance ||
                                        !this.state.selectedInsuranceContactId
                                      }
                                      renderOption={(props, option: DropdownOption) => (
                                        <li {...props} key={option.id}>
                                          <Chip
                                            label={`${option.description}${
                                              !option.isEnabled ? ' (Inactive)' : ''
                                            }`}
                                            style={{
                                              backgroundColor: !option.isEnabled
                                                ? '#e0e0e0'
                                                : '#3f51b5',
                                              color: !option.isEnabled
                                                ? '#000000'
                                                : '#ffffff',
                                              marginRight: 8,
                                              fontWeight: 500,
                                            }}
                                            size="small"
                                          />
                                        </li>
                                      )}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Additional Insurance Company Contacts"
                                          variant="outlined"
                                          name="additionalInsuranceContacts"
                                          InputLabelProps={{
                                            shrink: true,
                                            variant: 'outlined',
                                          }}
                                          fullWidth
                                        />
                                      )}
                                      renderTags={(value, getTagProps) =>
                                        value.map((option, index) => {
                                          const { key, ...tagProps } = getTagProps({
                                            index,
                                          })
                                          return (
                                            <Chip
                                              key={key}
                                              {...tagProps}
                                              label={`${option.description}${
                                                !option.isEnabled ? ' (Inactive)' : ''
                                              }`}
                                              style={{
                                                backgroundColor: !option.isEnabled
                                                  ? '#e0e0e0'
                                                  : '#3f51b5',
                                                color: !option.isEnabled
                                                  ? '#000000'
                                                  : '#ffffff',
                                              }}
                                              size="small"
                                            />
                                          )
                                        })
                                      }
                                    />
                                  </FormControl>
                                </Grid>
                              </Grid>
                              <Grid
                                container
                                item
                                lg={6}
                                spacing={2}
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <Grid item style={{ marginTop: '16px' }}>
                                  <Typography
                                    variant={'body1'}
                                    color="textSecondary"
                                    style={{
                                      height: '18px',
                                      paddingRight: '8px',
                                      marginBottom: '4px',
                                    }}
                                  >
                                    NURSE CASE MANAGER
                                  </Typography>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <Autocomplete
                                    size="small"
                                    id="nurseCaseManagerId"
                                    options={ncmCompanyOptions || []}
                                    getOptionLabel={(option: DropdownOption) =>
                                      option.description || ''
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                      option.id === value.id
                                    }
                                    value={this.getSelectedNCM(values) || null}
                                    onChange={(_, newValue) => {
                                      setFieldValue(
                                        'nurseCaseManager',
                                        newValue?.description || ''
                                      )
                                      setFieldValue(
                                        'nurseCaseManagerId',
                                        newValue?.id || undefined
                                      )
                                      this.updateNCMContacts(newValue)
                                    }}
                                    renderOption={(props, option: DropdownOption) => (
                                      <li {...props} key={option.id}>
                                        {option.description}
                                        {!option.isEnabled && ' (Inactive)'}
                                      </li>
                                    )}
                                    filterOptions={(options, state) => {
                                      const inputValue = state.inputValue.toLowerCase()
                                      return options.filter((option) =>
                                        option.description
                                          .toLowerCase()
                                          .includes(inputValue)
                                      )
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Nurse Case Manager Company"
                                        variant="outlined"
                                        error={!!errors.nurseCaseManagerId}
                                        helperText={errors.nurseCaseManagerId}
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    )}
                                  />
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <FormControl fullWidth variant="outlined">
                                    <InputLabel shrink={true} variant="outlined">
                                      Primary Nurse Case Manager Contact
                                    </InputLabel>
                                    <Autocomplete
                                      size="small"
                                      id="nurseCaseManagerContactId"
                                      options={this.props.ncmContactOptions || []}
                                      getOptionLabel={(option) =>
                                        option.description || ''
                                      }
                                      getOptionDisabled={(option: DropdownOption) =>
                                        !option.isEnabled
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                      }
                                      value={
                                        (this.props.ncmContactOptions || []).find(
                                          (x) => x.id === this.state.selectedNCMContactId
                                        ) || null
                                      }
                                      onChange={(_, newValue) => {
                                        this.setState({
                                          selectedNCMContactId: newValue?.id || undefined,
                                          selectedNCMContacts: new Map<string, string>(),
                                        })
                                      }}
                                      disabled={this.state.disableNCM}
                                      renderOption={(props, option: DropdownOption) => (
                                        <li {...props} key={option.id}>
                                          {option.description}
                                          {!option.isEnabled && ' (Inactive)'}
                                        </li>
                                      )}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Primary Nurse Case Manager Contact"
                                          variant="outlined"
                                          InputLabelProps={{ shrink: true }}
                                        />
                                      )}
                                    />
                                  </FormControl>
                                </Grid>
                                <Grid item md={12} xs={12}>
                                  <FormControl fullWidth variant="outlined">
                                    <InputLabel shrink={true} variant="outlined">
                                      Additional Nurse Case Manager Contacts
                                    </InputLabel>
                                    <Autocomplete
                                      size="small"
                                      multiple
                                      options={(
                                        this.props.ncmContactOptions || []
                                      ).filter(
                                        (x) =>
                                          x.id !== this.state.selectedNCMContactId &&
                                          !this.state.selectedNCMContacts.has(x.id)
                                      )}
                                      getOptionLabel={(option) =>
                                        option.description || ''
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                      }
                                      value={(() => {
                                        if (
                                          this.state.selectedNCMContacts.size === 0 ||
                                          !this.props.ncmContactOptions ||
                                          this.props.ncmContactOptions.length === 0
                                        ) {
                                          return []
                                        }

                                        return this.props.ncmContactOptions.filter(
                                          (option) =>
                                            this.state.selectedNCMContacts.has(option.id)
                                        )
                                      })()}
                                      onChange={(_, newValue) => {
                                        const newContacts = new Map<string, string>()
                                        newValue.forEach((option) => {
                                          newContacts.set(option.id, option.description)
                                        })
                                        this.setState(
                                          {
                                            selectedNCMContacts: newContacts,
                                          },
                                          () => {
                                            this.forceUpdateHandler(
                                              undefined,
                                              newContacts
                                            )
                                          }
                                        )
                                      }}
                                      disabled={
                                        this.state.disableNCM ||
                                        !this.state.selectedNCMContactId
                                      }
                                      renderOption={(props, option: DropdownOption) => (
                                        <li {...props} key={option.id}>
                                          <Chip
                                            label={`${option.description}${
                                              !option.isEnabled ? ' (Inactive)' : ''
                                            }`}
                                            style={{
                                              backgroundColor: !option.isEnabled
                                                ? '#e0e0e0'
                                                : '#3f51b5',
                                              color: !option.isEnabled
                                                ? '#000000'
                                                : '#ffffff',
                                              marginRight: 8,
                                              fontWeight: 500,
                                            }}
                                            size="small"
                                          />
                                        </li>
                                      )}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Additional Nurse Case Manager Contacts"
                                          variant="outlined"
                                          name="additionalNCMContacts"
                                          InputLabelProps={{
                                            shrink: true,
                                            variant: 'outlined',
                                          }}
                                          fullWidth
                                        />
                                      )}
                                      renderTags={(value, getTagProps) =>
                                        value.map((option, index) => {
                                          const { key, ...tagProps } = getTagProps({
                                            index,
                                          })
                                          return (
                                            <Chip
                                              key={key}
                                              {...tagProps}
                                              label={`${option.description}${
                                                !option.isEnabled ? ' (Inactive)' : ''
                                              }`}
                                              style={{
                                                backgroundColor: !option.isEnabled
                                                  ? '#e0e0e0'
                                                  : '#3f51b5',
                                                color: !option.isEnabled
                                                  ? '#000000'
                                                  : '#ffffff',
                                              }}
                                              size="small"
                                            />
                                          )
                                        })
                                      }
                                    />
                                  </FormControl>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item md={3} xs={12}>
                              <FullWidthField
                                InputProps={{
                                  style: { height: 40 },
                                }}
                                variant="outlined"
                                type="outlined"
                                name="referralCaller"
                                label="Referral Caller"
                              />
                            </Grid>
                            <Grid item md={4} xs={12}>
                              <FullWidthField
                                InputProps={{
                                  style: { height: 40 },
                                }}
                                variant="outlined"
                                type="outlined"
                                name="claimNumber"
                                label="Claim Number"
                              />
                            </Grid>
                            <Grid item md={2} xs={12}>
                              <Autocomplete
                                size="small"
                                id="jurisdictionId"
                                options={States}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) =>
                                  option.name === value.name
                                }
                                value={
                                  States.find(
                                    (item) =>
                                      item.name === this.state.selectedJurisdiction
                                  ) || null
                                }
                                onChange={(_, newValue) => {
                                  this.setState({
                                    selectedJurisdiction: newValue?.name || '',
                                  })
                                  this.forceUpdateHandler()
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Jurisdiction"
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                  />
                                )}
                                ListboxProps={{
                                  style: { maxHeight: 350 },
                                }}
                              />
                            </Grid>
                            <Grid item md={1} xs={12}>
                              <FullWidthField
                                InputProps={{
                                  style: { height: 40 },
                                }}
                                variant="outlined"
                                type="outlined"
                                name="initials"
                                label="initials"
                              />
                            </Grid>
                            <Grid item md={2} xs={12}>
                              <Field
                                name="health"
                                type="checkbox"
                                Label={{
                                  label: 'Self Pay',
                                }}
                                component={CheckboxWithLabel}
                              />
                            </Grid>
                          </CardWithTitle>
                        </Grid>
                      </Grid>
                    ) : (
                      <></>
                    )}
                  </Form>
                </>
              )
            }}
          </Formik>
        ) : (
          <></>
        )}
      </>
    )
  }
}

const InjectedEditReferral = inject<
  IStores,
  IEditReferralProps,
  Partial<IEditReferralProps>,
  any
>((stores: IStores) => ({
  employerContacts: stores.contacts.employerContacts,
  employerOptions: stores.employers.employerOptions,
  getEmployerOptions: stores.employers.getEmployerOptions,
  getPatientById: stores.patients.getPatientById,
  getReferralById: stores.referrals.getReferralWithContactsById,
  insuranceCompanies: stores.insuranceCompanies.insuranceCompanies,
  insuranceCompanyContacts: stores.contacts.insuranceCompanyContacts,
  isLoading: stores.referrals.isLoading,
  isModalOpen: stores.contacts.isModalOpen,
  nurseCaseManagerContacts: stores.contacts.nurseCaseManagerContacts,
  nurseCaseManagers: stores.nurseCaseManagers.nurseCaseManagers,
  referral: stores.referrals.selectedReferral,
  rerouteToPath: stores.global.rerouteToPath,
  saveReferral: stores.referrals.saveReferral,
  setSelectedReferral: stores.referrals.setSelectedReferral,
  insuranceCompanyOptions: stores.referrals.filteredInsuranceCompanyOptions,
  getInsuranceCompanyOptions: stores.referrals.getInsuranceCompanyOptions,
  filterInsuranceCompanyOptions: stores.referrals.filterInsuranceCompanyOptions,
  ncmCompanyOptions: stores.referrals.filteredNCMCompanyOptions,
  getNCMCompanyOptions: stores.referrals.getNCMCompanyOptions,
  filterNCMCompanyOptions: stores.referrals.filterNCMCompanyOptions,
  ncmContactOptions: stores.referrals.ncmContactOptions,
  insuranceCompanyContactOptions: stores.referrals.insuranceCompanyContactOptions,
  getInsuranceCompanyContactOptions: stores.referrals.getInsuranceCompanyContactOptions,
  getNCMContactOptions: stores.referrals.getNCMContactOptions,
  patient: stores.patients.selectedPatient,
}))(observer(EditReferral))

const EditReferralWithParams = (props: any) => {
  const { patientId, referralId } = useParams()
  const [isDirty, setIsDirty] = React.useState(false)
  const componentRef = React.useRef<EditReferral>(null)

  const {
    showCancelDialog,
    handleCancelClick,
    handleCancelConfirm,
    handleCancelDismiss,
  } = useUnsavedChangesWarning(isDirty)

  const handleCancel = () => {
    if (componentRef.current) {
      ;(componentRef.current as any).cancel()
    }
  }

  return (
    <>
      <InjectedEditReferral
        {...props}
        ref={componentRef}
        patientId={patientId!}
        referralId={referralId!}
        showCancelDialog={showCancelDialog}
        handleCancelClick={handleCancelClick}
        handleCancelConfirm={(onNavigate) => {
          handleCancelConfirm(onNavigate)
        }}
        handleCancelDismiss={handleCancelDismiss}
        setIsDirty={setIsDirty}
      />
      <UnsavedChangesDialog
        open={showCancelDialog}
        onConfirm={() => handleCancelConfirm(handleCancel)}
        onDismiss={handleCancelDismiss}
      />
    </>
  )
}

export default EditReferralWithParams
